/**
 * /zh/project-proposal-template — 项目提案模板中文 SEO 页。与
 * ProjectProposalTemplate.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function ProjectProposalTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/project-proposal-template"
      documentTitle="项目提案模板 + 示例 · Draftlize"
      documentDescription="一份可直接抄的项目提案模板——问题、目标、范围、方案、时间线、预算、成功标准、风险——并讲清怎么让进行中的项目不与已批准的提案脱节。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 项目提案</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            一份项目提案模板，<br/>
            <em>项目甩不掉它。</em>
          </h1>
          <p className="lede">
            你为一份<em>项目提案模板</em>（project proposal）而来——那套让一个项目被理解、被界定、被批准的结构。它在下面；抄走。但提案自带一个到期日：它基于一组关于范围、预算、时间线的假设被批准一次，然后从动工那一刻起，项目就开始偏离它。到第二个月，已批准的文档和进行中的项目悄悄各说各话，而没人说得出偏了多少。Draftlize 给你同样的提案，把它的假设做成卡片，在现实越过它们时去标记。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板</span>
            <h2 className="serif">八个部分。<em>随处可抄。</em></h2>
          </div>
          <p>一份项目提案回答决策者在点头前需要的八个问题。它对内部立项或客户提案都好使——形状一样。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '问题 / 背景', hint: '为什么要做', body: '为这个项目正名的情境或痛点，在任何方案之前。如果你没法不提你要做的工作就把问题说清，那你是在兜售一个找问题的方案。' },
            { k: '目标', hint: '成功是什么', body: '项目将达成什么，用可衡量的话。审批者真正签字认可的那些目标——保持少而具体，好让之后能据以评判「成没成」。' },
            { k: '范围（内与外）', hint: '边界', body: '什么包含在内、以及——人人爱省的那栏——什么明确在外。「范围之外」清单正是挡住「拖垮时间线的缓慢蔓延」的东西。点名一个非目标是决策，不是遗漏。' },
            { k: '方案 / 做法', hint: '怎么做', body: '你将怎么做——计划、方法、阶段。够证明它可行且想清楚了，而不是一份完整 spec。这里是评审者判断「你到底会不会交付」的地方。' },
            { k: '时间线与里程碑', hint: '何时', body: '带真实检查点的排期，而不只是一个截止日期。里程碑是你将知道「在不在轨道上」的地方——也是某个假设变了时，诚实地重新决策的节点。' },
            { k: '预算与资源', hint: '要花多少', body: '需要的钱、人、工具。把成本绑到各阶段，让审批者看到每一阶段在买什么，也让一次范围变更有一个看得见的价码。' },
            { k: '成功标准', hint: '完成且奏效', body: '所有人将如何认同项目成功了——指标和验收条件，在结果能左右判断之前就提前定好。' },
            { k: '风险与假设', hint: '什么会崩', body: '计划要成立必须为真的东西，以及威胁它的东西——诚实点名，附缓解。这里的假设，恰恰是进行中的项目最容易悄悄长出去的。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.04em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          一旦批准，提案就交棒给 <a href="/zh/prd-template" style={{ color: 'var(--amber)' }}>PRD</a> 和 <a href="/zh/technical-design-document" style={{ color: 'var(--amber)' }}>技术设计文档</a>——它们继承同样的漂移问题。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">提案和项目<em>为什么会分叉。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">批准一次，然后冻住</h3>
            <p>提案在某一刻、基于一组假设被签批。项目随后移动——范围调整、里程碑跳票、成本变化——而已批准的文档冻在原地。几周内它描述的就是一个不再存在的项目，而那正是管理层仍以为为真的版本。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">没人对账「进行中 vs 已批准」</h3>
            <p>把进行中的项目与提案对账，是一件干不过「真去推进项目」的杂活。于是缺口在无人盯防中扩大，直到一次状态评审或预算超支逼出这场对比——晚了，此时分叉已贵到难以收回。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">变了的假设无人追踪</h3>
            <p>提案里一个核心假设在第六周被证伪。计划的哪些部分压在它上面——时间线、预算、成功标准？在文档里，没有任何东西把它们连起来，于是假设变了，依赖它的承诺悄悄保持原样。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份提案，<em>接上线。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每节是卡片，每个假设有连接</h3>
            <p>在 Draftlize 里，提案的范围、预算、时间线、假设都是卡片——而时间线连到它所依赖的假设。计划不是一份冻结的 PDF；它是一个结构，其中「我们假设 X」和「因此时间线是 Y」之间的依赖是真实的边。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">假设一变，依赖它的标 stale</h3>
            <p>第六周那个假设崩了。所有压在它上面的承诺——时间线、预算、成功标准——自动标记 stale，就像构建系统让被改文件的下游全部失效一样。你刻意地重新决策，而不是在季度评审里才发现这场分叉。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 从「活」计划出发</h3>
            <p>当 AI agent 经 MCP 起草 PRD 或状态更新，它读到的是当前的提案——调整后的范围、修订后的时间线——而不是两个假设变了之前批准的版本。已批准的文档和真实项目，不再是两个不同的真相。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          提案批准一次。项目一直在动——而没有任何东西告诉那份文档：它描述的是一个不再存在的计划。
        </blockquote>
        <cite>留着八个部分，让假设在崩掉时去标记计划。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '一份项目提案该包含什么？', a: '问题或背景、可衡量的目标、范围（内与外）、方案、带里程碑的时间线、预算与资源、成功标准、风险与假设。最被省略、也最值钱的，是「范围之外」清单和被点名的假设。' },
            { q: '项目提案和 PRD 有什么区别？', a: '项目提案为「到底要不要做这个项目」立论——向决策者证明投入、范围和成本是值得的；PRD 在项目获批后定义要做什么。提案拿到「同意」，PRD 把「同意」变成团队能做的东西。' },
            { q: '项目提案该写多长？', a: '在回答决策者需要的八个问题的前提下，越短越好。内部立项常常一到三页就够；大型客户合作可能需要更多。长度不是目标——一个清晰、可据以决策的立论才是。' },
            { q: '怎么让项目不偏离它的提案？', a: '把计划的承诺连到它们所依赖的假设，并在里程碑、而不只是结尾时回看它们。失败方式是：提案批准一次，之后从不与进行中的项目对账，直到一次评审逼着对账。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">让计划与项目保持同步<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用上面的模板，或在 Draftlize 里把提案建成卡片，让时间线和预算连着它们所依赖的假设——这样一个假设崩了，依赖它的承诺就标 stale，而不是悄悄活得比计划还久。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
